import { useGLTF, useTexture } from '@react-three/drei';
import { useEffect, useMemo } from 'react';
import { DoubleSide, Mesh, MeshStandardMaterial, SRGBColorSpace } from 'three';

import {
  registerObjects,
  SOULSTACK_ORIGINALS_AUTHOR,
  SOULSTACK_ORIGINALS_BUNDLE,
  type ObjectDefinition,
  type ParamSchema,
} from '../../registry/objectRegistry';
import {
  registerRenderers,
  type AssetRenderer,
} from '../../registry/objectRenderers';
import type { MapObjectInstance } from '../../schema/map';
import { registerCoreCharacters } from '../../../characters/core/NeutralBiped';
import { registerParamSchemas } from '../../../editor/validation/paramSchemas';
import { ParticleEmitter } from '../../../runtime/fx/ParticleEmitter';
import { FX_EMITTER_PRESETS, hashSeed, type FxEmitterPreset } from '../../../runtime/fx/particles';

// ── Param helpers ──────────────────────────────────────────────────────────────
function num(instance: MapObjectInstance, key: string, fallback: number): number {
  const v = instance.params?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
function str(instance: MapObjectInstance, key: string, fallback: string): string {
  const v = instance.params?.[key];
  return typeof v === 'string' ? v : fallback;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * Standard-material props honouring the instance's material params: `color`,
 * `roughness`, `metalness` and `emissive` (0..1 intensity, glowing in the
 * surface colour). Fallbacks keep each renderer's authored look.
 */
function matProps(
  instance: MapObjectInstance,
  fallbackColor: string,
  fallbackRoughness = 0.8,
  fallbackMetalness = 0.05,
) {
  const color = str(instance, 'color', fallbackColor);
  const emissive = clamp01(num(instance, 'emissive', 0));
  return {
    color,
    roughness: clamp01(num(instance, 'roughness', fallbackRoughness)),
    metalness: clamp01(num(instance, 'metalness', fallbackMetalness)),
    emissive: emissive > 0 ? color : '#000000',
    emissiveIntensity: emissive,
  };
}

/** Editable material params shared by every solid core-pack object. */
const MATERIAL_PARAM_SCHEMAS: Record<string, ParamSchema> = {
  color: { type: 'color' },
  roughness: { type: 'number', min: 0, max: 1, step: 0.01 },
  metalness: { type: 'number', min: 0, max: 1, step: 0.01 },
  emissive: { type: 'number', min: 0, max: 1, step: 0.01 },
  material: { type: 'string', maxLength: 64 },
};

// Neutral PBR palette so a fresh project looks clean and readable.
const NEUTRAL = '#b8bdc6';
const NEUTRAL_WOOD = '#b08c5a';
const NEUTRAL_METAL = '#9aa3ad';

// ── Definitions (registered on top of the engine built-ins) ─────────────────────
const CORE_DEFINITIONS: Record<string, ObjectDefinition> = {
  prim_box: {
    id: 'prim_box', name: 'Box', category: 'structural', description: 'Parametric box primitive.',
    bounds: { size: [1, 1, 1], blockerFootprint: [1, 1] },
    defaultParams: { width: 1, height: 1, depth: 1, color: NEUTRAL },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['primitive'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  prim_cylinder: {
    id: 'prim_cylinder', name: 'Cylinder', category: 'structural', description: 'Parametric cylinder primitive.',
    bounds: { size: [1, 1, 1], blockerFootprint: [1, 1] },
    defaultParams: { radius: 0.5, height: 1, color: NEUTRAL },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['primitive'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  prim_sphere: {
    id: 'prim_sphere', name: 'Sphere', category: 'structural', description: 'Parametric sphere primitive.',
    bounds: { size: [1, 1, 1] },
    defaultParams: { radius: 0.5, color: NEUTRAL },
    editor: { pivot: 'center', snap: 0.1, tags: ['primitive'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  prim_plane: {
    id: 'prim_plane', name: 'Plane', category: 'structural', description: 'Flat plane primitive.',
    bounds: { size: [1, 0.02, 1] },
    defaultParams: { width: 1, depth: 1, color: NEUTRAL },
    editor: { pivot: 'center', snap: 0.1, tags: ['primitive'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  arch_wall: {
    id: 'arch_wall', name: 'Wall', category: 'structural', description: 'Straight wall segment.',
    bounds: { size: [2, 2.6, 0.2], blockerFootprint: [2, 0.2] },
    defaultParams: { width: 2, height: 2.6, thickness: 0.2, color: NEUTRAL },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['architecture', 'wall-fit'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  arch_floor_tile: {
    id: 'arch_floor_tile', name: 'Floor Tile', category: 'structural', description: 'Floor tile / platform.',
    bounds: { size: [2, 0.1, 2] },
    defaultParams: { width: 2, depth: 2, color: '#8b9099' },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['architecture', 'floor'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  arch_column: {
    id: 'arch_column', name: 'Column', category: 'structural', description: 'Vertical support column.',
    bounds: { size: [0.4, 2.8, 0.4], blockerFootprint: [0.5, 0.5] },
    defaultParams: { radius: 0.2, height: 2.8, color: NEUTRAL },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['architecture'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  arch_doorway: {
    id: 'arch_doorway', name: 'Doorway', category: 'structural', description: 'Door frame opening.',
    bounds: { size: [1.4, 2.4, 0.24], blockerFootprint: [0.3, 0.24] },
    defaultParams: { width: 1.4, height: 2.4, color: NEUTRAL },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['architecture', 'wall-fit'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  furn_table: {
    id: 'furn_table', name: 'Table', category: 'furniture', description: 'Simple four-legged table.',
    bounds: { size: [1.4, 0.78, 0.8], blockerFootprint: [1.4, 0.8] },
    defaultParams: { width: 1.4, depth: 0.8, color: NEUTRAL_WOOD },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['furniture'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  furn_chair: {
    id: 'furn_chair', name: 'Chair', category: 'furniture', description: 'Simple chair.',
    bounds: { size: [0.5, 0.9, 0.5], blockerFootprint: [0.5, 0.5] },
    defaultParams: { color: NEUTRAL_WOOD },
    editor: { pivot: 'base-center', snap: 0.05, tags: ['furniture'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  furn_crate: {
    id: 'furn_crate', name: 'Crate', category: 'furniture', description: 'Stackable wooden crate.',
    bounds: { size: [0.8, 0.8, 0.8], blockerFootprint: [0.8, 0.8] },
    defaultParams: { size: 0.8, color: NEUTRAL_WOOD },
    editor: { pivot: 'base-center', snap: 0.05, tags: ['furniture', 'cargo'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  furn_shelf: {
    id: 'furn_shelf', name: 'Shelf', category: 'furniture', description: 'Open shelving unit.',
    bounds: { size: [1.2, 1.8, 0.4], blockerFootprint: [1.2, 0.4] },
    defaultParams: { width: 1.2, height: 1.8, shelves: 3, color: NEUTRAL_METAL },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['furniture', 'storage'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  gltf_model: {
    id: 'gltf_model', name: 'Model (glTF)', category: 'structural',
    description: 'User-uploaded glTF/GLB model rendered from a URL.',
    bounds: { size: [1, 1, 1] },
    defaultParams: { url: '', scale: 1 },
    editor: { pivot: 'base-center', snap: 0.05, tags: ['model', 'upload'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  fx_emitter: {
    id: 'fx_emitter', name: 'FX Emitter', category: 'effects',
    description: 'Particle emitter playing an FX preset in place.',
    bounds: { size: [0.6, 1.2, 0.6] },
    defaultParams: { preset: 'spark_burst', rate: 40, size: 36, color: '#71b4fe' },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['fx', 'particles'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
};

// ── Renderers ───────────────────────────────────────────────────────────────────
function GltfModel({ instance }: { instance: MapObjectInstance }) {
  const url = str(instance, 'url', '');
  const scale = num(instance, 'scale', 1);
  if (!url) return null;
  return <GltfModelLoaded url={url} scale={scale} instance={instance} />;
}

/** The subset of `matProps`' fields actually set on this instance - `undefined`
 * for anything never authored, so an unset field leaves the glTF's own
 * material untouched instead of flattening it to a default grey. Texture map
 * fields carry asset URLs (set by FX & Materials' "Assign material" flow,
 * docs/3d-content-pipeline/roadmap.md 3.2 Phase 1) rather than loaded
 * textures - GltfModelLoaded resolves those. */
function gltfMaterialOverrides(instance: MapObjectInstance): {
  color?: string;
  roughness?: number;
  metalness?: number;
  emissive?: string;
  emissiveIntensity?: number;
  map?: string;
  normalMap?: string;
  roughnessMap?: string;
  metalnessMap?: string;
} | null {
  const p = instance.params;
  if (
    !p ||
    (p.color === undefined && p.roughness === undefined && p.metalness === undefined && p.emissive === undefined &&
      p.albedoMap === undefined && p.normalMap === undefined && p.roughnessMap === undefined && p.metalnessMap === undefined)
  ) {
    return null;
  }
  const color = typeof p.color === 'string' ? p.color : undefined;
  const emissiveIntensity = typeof p.emissive === 'number' ? clamp01(p.emissive) : undefined;
  return {
    color,
    roughness: typeof p.roughness === 'number' ? clamp01(p.roughness) : undefined,
    metalness: typeof p.metalness === 'number' ? clamp01(p.metalness) : undefined,
    emissive: emissiveIntensity ? (color ?? '#ffffff') : undefined,
    emissiveIntensity,
    map: typeof p.albedoMap === 'string' ? p.albedoMap : undefined,
    normalMap: typeof p.normalMap === 'string' ? p.normalMap : undefined,
    roughnessMap: typeof p.roughnessMap === 'string' ? p.roughnessMap : undefined,
    metalnessMap: typeof p.metalnessMap === 'string' ? p.metalnessMap : undefined,
  };
}

function GltfModelLoaded({ url, scale, instance }: { url: string; scale: number; instance: MapObjectInstance }) {
  // useGLTF suspends until loaded; the host wraps placed objects in a boundary.
  const gltf = useGLTF(url);
  const params = instance.params;
  // Memoised on the individual material param fields (the same set the
  // `scene` useMemo below already keys on) rather than left as a plain call
  // re-run every render, so `overrides` itself only changes identity when
  // one of those fields actually does.
  const overrides = useMemo(
    () => gltfMaterialOverrides(instance),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the individual param fields `gltfMaterialOverrides` reads, not `instance`/`params` as a whole (a fresh object every render).
    [params?.color, params?.roughness, params?.metalness, params?.emissive, params?.albedoMap, params?.normalMap, params?.roughnessMap, params?.metalnessMap],
  );
  // useTexture accepts a partial map record and suspends until every entry
  // loads; an empty record (no map overrides set) resolves immediately.
  // Memoised on `overrides` (now itself stable - see above) so `mapUrls`
  // keeps the same identity across renders where the underlying override
  // URLs haven't changed - a fresh object every render (the pre-fix
  // behaviour) broke drei's own internal `useTexture`/`useLoader`
  // memoisation (keyed off this object), which in turn made `textures`
  // below a new object every render too, and that fed straight into the
  // `scene` useMemo's own dependency array, forcing `gltf.scene.clone()`
  // plus a full material `.clone()` on every single render of a placed
  // instance regardless of whether anything material-related had actually
  // changed.
  const mapUrls = useMemo(() => {
    const urls: Record<string, string> = {};
    if (overrides?.map) urls.map = overrides.map;
    if (overrides?.normalMap) urls.normalMap = overrides.normalMap;
    if (overrides?.roughnessMap) urls.roughnessMap = overrides.roughnessMap;
    if (overrides?.metalnessMap) urls.metalnessMap = overrides.metalnessMap;
    return urls;
  }, [overrides]);
  const textures = useTexture(mapUrls);
  const scene = useMemo(() => {
    // Object3D#clone() copies the hierarchy but not materials - meshes keep
    // the SAME material references as the cached source, so mutating them
    // in place would bleed across every other placed instance of this GLB.
    const clone = gltf.scene.clone();
    if (!overrides) return clone;
    clone.traverse((node) => {
      const mesh = node as Mesh;
      if (!mesh.isMesh) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mesh.material = materials.map((material) => {
        const std = material as MeshStandardMaterial;
        if (!std.isMeshStandardMaterial) return material;
        const next = std.clone();
        // Tags this clone (not the shared, cached source material every
        // other placed instance of this GLB also references) so the
        // disposal effect below can safely release only what this
        // component itself allocated.
        next.userData.coreObjectOverrideClone = true;
        if (overrides.color !== undefined) next.color.set(overrides.color);
        if (overrides.roughness !== undefined) next.roughness = overrides.roughness;
        if (overrides.metalness !== undefined) next.metalness = overrides.metalness;
        if (overrides.emissiveIntensity !== undefined) {
          next.emissive.set(overrides.emissive!);
          next.emissiveIntensity = overrides.emissiveIntensity;
        }
        if (overrides.map !== undefined) {
          next.map = textures.map ?? null;
          // A raw imperative assignment like this bypasses R3F's automatic
          // sRGB colorSpace conversion (which only fires for JSX-authored
          // props, e.g. <meshStandardMaterial map={...}>), so a loaded
          // albedo texture would otherwise stay linear-interpreted forever
          // and render washed out. Only `map` (colour data) needs this -
          // normalMap/roughnessMap/metalnessMap below are correctly linear
          // data and must NOT get this treatment.
          if (next.map) next.map.colorSpace = SRGBColorSpace;
        }
        if (overrides.normalMap !== undefined) next.normalMap = textures.normalMap ?? null;
        if (overrides.roughnessMap !== undefined) next.roughnessMap = textures.roughnessMap ?? null;
        if (overrides.metalnessMap !== undefined) next.metalnessMap = textures.metalnessMap ?? null;
        next.needsUpdate = true;
        return next;
      });
    });
    return clone;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-derive only when the source scene, the material params, or the loaded textures actually change.
  }, [gltf, params?.color, params?.roughness, params?.metalness, params?.emissive, params?.albedoMap, params?.normalMap, params?.roughnessMap, params?.metalnessMap, textures]);
  // Rendered via <primitive>, which R3F never auto-disposes, and only the
  // clones this component made itself (tagged above) are safe to release -
  // the geometry and any untouched material are still the shared, cached
  // ones every other placed instance of this GLB also references.
  useEffect(() => () => {
    scene.traverse((node) => {
      const mesh = node as Mesh;
      if (!mesh.isMesh) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of materials) {
        if (material?.userData.coreObjectOverrideClone) material.dispose();
      }
    });
  }, [scene]);
  return <primitive object={scene} scale={scale} />;
}

const CORE_RENDERERS: Record<string, AssetRenderer> = {
  // Primitives
  prim_box: ({ instance }) => (
    <mesh position={[0, num(instance, 'height', 1) / 2, 0]} castShadow receiveShadow>
      <boxGeometry args={[num(instance, 'width', 1), num(instance, 'height', 1), num(instance, 'depth', 1)]} />
      <meshStandardMaterial {...matProps(instance, NEUTRAL)} />
    </mesh>
  ),
  prim_cylinder: ({ instance }) => (
    <mesh position={[0, num(instance, 'height', 1) / 2, 0]} castShadow receiveShadow>
      <cylinderGeometry args={[num(instance, 'radius', 0.5), num(instance, 'radius', 0.5), num(instance, 'height', 1), 24]} />
      <meshStandardMaterial {...matProps(instance, NEUTRAL)} />
    </mesh>
  ),
  prim_sphere: ({ instance }) => (
    <mesh position={[0, num(instance, 'radius', 0.5), 0]} castShadow receiveShadow>
      <sphereGeometry args={[num(instance, 'radius', 0.5), 24, 20]} />
      <meshStandardMaterial {...matProps(instance, NEUTRAL, 0.7)} />
    </mesh>
  ),
  prim_plane: ({ instance }) => (
    <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <planeGeometry args={[num(instance, 'width', 1), num(instance, 'depth', 1)]} />
      <meshStandardMaterial {...matProps(instance, NEUTRAL, 0.9)} side={DoubleSide} />
    </mesh>
  ),

  // Architecture
  arch_wall: ({ instance }) => {
    const h = num(instance, 'height', 2.6);
    return (
      <mesh position={[0, h / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[num(instance, 'width', 2), h, num(instance, 'thickness', 0.2)]} />
        <meshStandardMaterial {...matProps(instance, NEUTRAL, 0.85, 0.04)} />
      </mesh>
    );
  },
  arch_floor_tile: ({ instance }) => (
    <mesh position={[0, 0.05, 0]} receiveShadow>
      <boxGeometry args={[num(instance, 'width', 2), 0.1, num(instance, 'depth', 2)]} />
      <meshStandardMaterial {...matProps(instance, '#8b9099', 0.92, 0.03)} />
    </mesh>
  ),
  arch_column: ({ instance }) => {
    const h = num(instance, 'height', 2.8);
    return (
      <mesh position={[0, h / 2, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[num(instance, 'radius', 0.2), num(instance, 'radius', 0.2), h, 20]} />
        <meshStandardMaterial {...matProps(instance, NEUTRAL, 0.8, 0.06)} />
      </mesh>
    );
  },
  arch_doorway: ({ instance }) => {
    const w = num(instance, 'width', 1.4);
    const h = num(instance, 'height', 2.4);
    const jamb = 0.16;
    const material = matProps(instance, NEUTRAL, 0.85);
    return (
      <group>
        <mesh position={[-w / 2 + jamb / 2, h / 2, 0]} castShadow receiveShadow>
          <boxGeometry args={[jamb, h, 0.24]} />
          <meshStandardMaterial {...material} />
        </mesh>
        <mesh position={[w / 2 - jamb / 2, h / 2, 0]} castShadow receiveShadow>
          <boxGeometry args={[jamb, h, 0.24]} />
          <meshStandardMaterial {...material} />
        </mesh>
        <mesh position={[0, h - jamb / 2, 0]} castShadow receiveShadow>
          <boxGeometry args={[w, jamb, 0.24]} />
          <meshStandardMaterial {...material} />
        </mesh>
      </group>
    );
  },

  // Furniture
  furn_table: ({ instance }) => {
    const w = num(instance, 'width', 1.4);
    const d = num(instance, 'depth', 0.8);
    const material = matProps(instance, NEUTRAL_WOOD, 0.7);
    const legY = 0.36;
    const legs: [number, number][] = [
      [w / 2 - 0.08, d / 2 - 0.08], [-(w / 2 - 0.08), d / 2 - 0.08],
      [w / 2 - 0.08, -(d / 2 - 0.08)], [-(w / 2 - 0.08), -(d / 2 - 0.08)],
    ];
    return (
      <group>
        <mesh position={[0, 0.74, 0]} castShadow receiveShadow>
          <boxGeometry args={[w, 0.08, d]} />
          <meshStandardMaterial {...material} />
        </mesh>
        {legs.map(([x, z], i) => (
          <mesh key={i} position={[x, legY, z]} castShadow receiveShadow>
            <boxGeometry args={[0.08, 0.72, 0.08]} />
            <meshStandardMaterial {...material} />
          </mesh>
        ))}
      </group>
    );
  },
  furn_chair: ({ instance }) => {
    const material = matProps(instance, NEUTRAL_WOOD, 0.75);
    return (
      <group>
        <mesh position={[0, 0.45, 0]} castShadow receiveShadow>
          <boxGeometry args={[0.44, 0.06, 0.44]} />
          <meshStandardMaterial {...material} />
        </mesh>
        <mesh position={[0, 0.7, -0.19]} castShadow receiveShadow>
          <boxGeometry args={[0.44, 0.5, 0.06]} />
          <meshStandardMaterial {...material} />
        </mesh>
        {([[0.18, 0.18], [-0.18, 0.18], [0.18, -0.18], [-0.18, -0.18]] as [number, number][]).map(([x, z], i) => (
          <mesh key={i} position={[x, 0.22, z]} castShadow receiveShadow>
            <boxGeometry args={[0.05, 0.44, 0.05]} />
            <meshStandardMaterial {...material} />
          </mesh>
        ))}
      </group>
    );
  },
  furn_crate: ({ instance }) => {
    const s = num(instance, 'size', 0.8);
    return (
      <mesh position={[0, s / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[s, s, s]} />
        <meshStandardMaterial {...matProps(instance, NEUTRAL_WOOD, 0.85, 0.04)} />
      </mesh>
    );
  },
  furn_shelf: ({ instance }) => {
    const w = num(instance, 'width', 1.2);
    const h = num(instance, 'height', 1.8);
    const shelves = Math.max(2, Math.round(num(instance, 'shelves', 3)));
    const material = matProps(instance, NEUTRAL_METAL, 0.7, 0.2);
    return (
      <group>
        {[-w / 2 + 0.04, w / 2 - 0.04].map((x, i) => (
          <mesh key={`post-${i}`} position={[x, h / 2, 0]} castShadow receiveShadow>
            <boxGeometry args={[0.06, h, 0.4]} />
            <meshStandardMaterial {...material} />
          </mesh>
        ))}
        {Array.from({ length: shelves }, (_, i) => {
          const y = (h / (shelves - 1)) * i;
          return (
            <mesh key={`shelf-${i}`} position={[0, Math.min(y, h - 0.03), 0]} castShadow receiveShadow>
              <boxGeometry args={[w, 0.04, 0.4]} />
              <meshStandardMaterial {...material} />
            </mesh>
          );
        })}
      </group>
    );
  },

  // Lights (engine built-in defs) - real lights plus a small visible gizmo.
  light_point_warm: ({ instance }) => (
    <group>
      <pointLight intensity={num(instance, 'intensity', 1.2)} distance={num(instance, 'range', 8)} color={str(instance, 'color', '#ffd9a8')} castShadow />
      <mesh><sphereGeometry args={[0.08, 12, 12]} /><meshBasicMaterial color={str(instance, 'color', '#ffd9a8')} /></mesh>
    </group>
  ),
  light_point_cool: ({ instance }) => (
    <group>
      <pointLight intensity={num(instance, 'intensity', 1.0)} distance={num(instance, 'range', 6)} color={str(instance, 'color', '#c8e0ff')} castShadow />
      <mesh><sphereGeometry args={[0.08, 12, 12]} /><meshBasicMaterial color={str(instance, 'color', '#c8e0ff')} /></mesh>
    </group>
  ),
  light_spot: ({ instance }) => (
    <group>
      <spotLight intensity={num(instance, 'intensity', 2.0)} distance={num(instance, 'range', 10)} angle={num(instance, 'angle', 0.4)} penumbra={0.4} color={str(instance, 'color', '#fff8e8')} castShadow />
      <mesh><coneGeometry args={[0.1, 0.2, 12]} /><meshBasicMaterial color={str(instance, 'color', '#fff8e8')} /></mesh>
    </group>
  ),

  // Markers - small posts (editor aid; harmless in play).
  marker_spawn: () => <MarkerPost color="#54e08a" />,
  marker_checkpoint: () => <MarkerPost color="#5ab0ff" />,
  marker_event: () => <MarkerPost color="#d98cff" />,

  // Trigger volumes - translucent boxes.
  trigger_teleport: ({ instance }) => <TriggerVolume size={defSize(instance, [2, 2.4, 2])} color="#5ab0ff" />,
  trigger_event: ({ instance }) => <TriggerVolume size={defSize(instance, [2, 2.4, 2])} color="#e0c054" />,
  trigger_jumpscare: ({ instance }) => <TriggerVolume size={defSize(instance, [1.6, 2.4, 1.6])} color="#ff5a6a" />,

  // Decals - flat translucent quads on the floor/wall.
  decal_wet_floor: ({ instance }) => <Decal color="#3a4a5a" opacity={num(instance, 'opacity', 0.6)} size={[2, 1.4]} />,
  decal_stain: ({ instance }) => <Decal color="#1a1410" opacity={num(instance, 'opacity', 0.7)} size={[1.2, 0.9]} />,
  decal_crack: ({ instance }) => <Decal color="#101216" opacity={num(instance, 'opacity', 0.8)} size={[0.8, 1.2]} vertical />,

  // glTF upload
  gltf_model: ({ instance }) => <GltfModel instance={instance} />,

  // FX particle emitter - deterministic per instance id.
  fx_emitter: ({ instance }) => {
    const raw = str(instance, 'preset', 'spark_burst');
    const preset = (FX_EMITTER_PRESETS as readonly string[]).includes(raw)
      ? (raw as FxEmitterPreset)
      : 'spark_burst';
    return (
      <ParticleEmitter
        preset={preset}
        rate={num(instance, 'rate', 40)}
        size={num(instance, 'size', 36)}
        colour={str(instance, 'color', '#71b4fe')}
        seed={hashSeed(instance.id)}
      />
    );
  },
};

function MarkerPost({ color }: { color: string }) {
  return (
    <group>
      <mesh position={[0, 0.9, 0]}>
        <cylinderGeometry args={[0.04, 0.04, 1.8, 8]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.5} />
      </mesh>
      <mesh position={[0, 1.85, 0]}>
        <sphereGeometry args={[0.12, 16, 16]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.8} />
      </mesh>
    </group>
  );
}

function TriggerVolume({ size, color }: { size: [number, number, number]; color: string }) {
  return (
    <mesh position={[0, size[1] / 2, 0]}>
      <boxGeometry args={size} />
      <meshStandardMaterial color={color} transparent opacity={0.16} emissive={color} emissiveIntensity={0.25} depthWrite={false} />
    </mesh>
  );
}

function Decal({ color, opacity, size, vertical }: { color: string; opacity: number; size: [number, number]; vertical?: boolean }) {
  return (
    <mesh rotation={vertical ? [0, 0, 0] : [-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
      <planeGeometry args={size} />
      <meshStandardMaterial color={color} transparent opacity={opacity} roughness={0.95} side={DoubleSide} depthWrite={false} />
    </mesh>
  );
}

function defSize(instance: MapObjectInstance, fallback: [number, number, number]): [number, number, number] {
  return [num(instance, 'width', fallback[0]), num(instance, 'height', fallback[1]), num(instance, 'depth', fallback[2])];
}

let registered = false;

/**
 * Register the engine's neutral starter content: definitions for primitives,
 * architecture, and furniture, plus renderers for those and the built-in
 * lights/markers/triggers/decals, and the glTF upload renderer. Call once at
 * Studio/app startup. Idempotent.
 */
export function registerCorePack(): void {
  if (registered) return;
  registered = true;
  registerObjects(CORE_DEFINITIONS);
  registerRenderers(CORE_RENDERERS);
  registerCoreCharacters();

  // Every solid core object accepts the shared material params; the emitter
  // exposes its own editable schema for the registry inspector.
  const materialSchemas: Record<string, Record<string, ParamSchema>> = {};
  for (const id of Object.keys(CORE_DEFINITIONS)) {
    // fx_emitter gets its own schema below (preset/rate/size/colour, not PBR
    // material params). Every other solid object, including gltf_model now
    // that GltfModelLoaded actually applies these params, accepts them.
    if (id === 'fx_emitter') continue;
    materialSchemas[id] = MATERIAL_PARAM_SCHEMAS;
  }
  materialSchemas.fx_emitter = {
    preset: { type: 'enum', options: FX_EMITTER_PRESETS },
    rate: { type: 'number', min: 1, max: 200, step: 1, integer: true },
    size: { type: 'number', min: 4, max: 100, step: 1 },
    color: { type: 'color' },
  };
  registerParamSchemas(materialSchemas);
}

export { CORE_DEFINITIONS, CORE_RENDERERS, gltfMaterialOverrides };
